import React from "react";
import { BrandTheme, StyleConfig, ThemeConfig } from "../types/SurveyTypes";
import { extractGradientColors, hexToGlassColor } from "../../../../general";

export class ThemeManager {
  private brandTheme: BrandTheme;
  private styleConfig: StyleConfig;
  private themeConfig: ThemeConfig;
  private questThemeData: any;

  constructor(
    brandTheme: BrandTheme = {},
    styleConfig: StyleConfig = {},
    themeConfig: ThemeConfig = {},
    questThemeData: any = {}
  ) {
    this.brandTheme = brandTheme;
    this.styleConfig = styleConfig;
    this.themeConfig = themeConfig;
    this.questThemeData = questThemeData;
  }

  getTextInputErrorText(): string {
    return this.styleConfig?.TextError?.text || "Invalid text format";
  }

  getTextInputErrorStyles(): React.CSSProperties {
    return {
      ...this.styleConfig?.TextError?.errorStyle,
    };
  }

  getEmailIconColor(): string {
    return (
      this.styleConfig?.Input?.color ||
      this?.brandTheme?.titleColor ||
      this?.themeConfig?.primaryColor ||
      "#B9B9B9"
    );
  }

  getEmailInputErrorText(): string {
    return this.styleConfig?.EmailError?.text || "Invalid text format";
  }

  getEMailInputErrorStyles(): React.CSSProperties {
    return {
      ...this.styleConfig?.EmailError?.errorStyle,
    };
  }
  getTextInputStyles(): React.CSSProperties {
    return {
      borderColor: this.styleConfig?.Input?.borderColor,
      color:
        this.styleConfig?.Input?.color ||
        this.brandTheme?.titleColor ||
        this.themeConfig?.primaryColor,
      borderRadius: this.questThemeData?.borderRadius || this.brandTheme?.borderRadius,
      ...this.styleConfig?.Input,
    };
  }

  getSingleChoiceHoverBackground(): string {
    return this.styleConfig?.SingleChoice?.hoverBackground;
  }

  getTextAreaInputStyles(): React.CSSProperties {
    return {
      borderColor: this.styleConfig?.TextArea?.borderColor,
      color:
        this.styleConfig?.TextArea?.color ||
        this.brandTheme?.titleColor ||
        this.themeConfig?.primaryColor,
      borderRadius: this.questThemeData?.borderRadius || this.brandTheme?.borderRadius,
      ...this.styleConfig?.TextArea,
    };
  }

  getTextAreaInputErrorText(): string {
    return this.styleConfig?.TextAreaError?.text || "Invalid text format";
  }

  getTextAreaInputErrorStyles(): React.CSSProperties {
    return {
      ...this.styleConfig?.TextAreaError?.errorStyle,
    };
  }

  getNumberInputErrorText(): string {
    return this.styleConfig?.NumberError?.text || "Invalid text format";
  }

  getNumberInputErrorStyles(): React.CSSProperties {
    return {
      ...this.styleConfig?.NumberError?.errorStyle,
    };
  }
  getFormStyles(): React.CSSProperties {
    return {
      background:
        this.styleConfig?.Form?.backgroundColor ||
        this.brandTheme?.background ||
        this.themeConfig?.backgroundColor,
      borderRadius:
        this.styleConfig?.Form?.borderRadius ||
        this.questThemeData?.borderRadius ||
        this.brandTheme?.borderRadius,
      height: this.styleConfig?.Form?.height || "auto",
      fontFamily:
        this.brandTheme?.fontFamily ||
        this.themeConfig.fontFamily ||
        "'Figtree', sans-serif",
      ...this.styleConfig?.Form,
    };
  }

  getLabelStyles(): React.CSSProperties {
    return {
      color:
        this.styleConfig?.Label?.color ||
        this.brandTheme?.contentColor ||
        this.themeConfig?.primaryColor,
      ...this.styleConfig?.Label,
    };
  }

  getRatingStyles(): React.CSSProperties {
    return {
      ...this.styleConfig?.Rating,
      background: hexToGlassColor(this?.brandTheme?.primaryColor || this.questThemeData?.accentColor, (this?.brandTheme?.primaryColor || this.questThemeData?.accentColor) ? 0.7 : 1),
      borderRadius: this.questThemeData?.borderRadius || this.brandTheme?.borderRadius,
    };
  }

  getInputStyles(): React.CSSProperties {
    return {
      borderColor:
        this.styleConfig?.Input?.borderColor || this.themeConfig?.borderColor,
      color:
        this.styleConfig?.Input?.color ||
        this.brandTheme?.primaryColor ||
        this.themeConfig?.primaryColor,
      ...this.styleConfig?.Input,
    };
  }

  getTextAreaStyles(): React.CSSProperties {
    return {
      borderColor:
        this.styleConfig?.TextArea?.borderColor ||
        this.themeConfig?.borderColor,
      color:
        this.styleConfig?.TextArea?.color ||
        this.brandTheme?.primaryColor ||
        this.themeConfig?.primaryColor,
      ...this.styleConfig?.TextArea,
    };
  }

  getSingleChoiceStyles(): React.CSSProperties {
    return {
      borderColor:
        this.styleConfig?.SingleChoice?.style?.borderColor ||
        this.themeConfig?.borderColor,
      color:
        this.styleConfig?.SingleChoice?.style?.color ||
        this.brandTheme?.titleColor ||
        this.themeConfig?.primaryColor,
      borderRadius: this.questThemeData?.borderRadius || this.brandTheme?.borderRadius,
      ...this.styleConfig?.SingleChoice?.style,
    };
  }

  getSingleChoiceSelectedStyles(): React.CSSProperties {
    return {
      accentColor:
        this.styleConfig?.SingleChoice?.selectedStyle?.accentColor ||
        this.brandTheme?.primaryColor ||
        this.questThemeData?.accentColor ||
        this.themeConfig?.primaryColor,
      ...this.styleConfig?.SingleChoice?.selectedStyle,
    };
  }
  getSingleChoiceHoverStyles(): React.CSSProperties {
    return {
      background: this.styleConfig?.SingleChoice?.hoverBackground || hexToGlassColor(this.brandTheme?.primaryColor || this.questThemeData?.accentColor, 0.5),
    };
  }
  getMultiChoiceStyles(): React.CSSProperties {
    return {
      borderColor:
        this.styleConfig?.MultiChoice?.style?.borderColor ||
        this.themeConfig?.borderColor,
      color:
        this.styleConfig?.MultiChoice?.style?.color ||
        this.themeConfig?.secondaryColor ||
        this.brandTheme?.titleColor,
      ...this.styleConfig?.MultiChoice?.style,
    };
  }

  getMultiChoiceSelectedStyles(): React.CSSProperties {
    return {
      borderColor:
        this.styleConfig?.MultiChoice?.selectedStyle?.borderColor ||
        this.brandTheme?.primaryColor ||
        this.questThemeData?.accentColor,
      color:
        this.styleConfig?.MultiChoice?.selectedStyle?.color ||
        this.brandTheme?.primaryColor ||
        this.questThemeData?.accentColor ||
        this.themeConfig?.primaryColor,
      accentColor:
        this.styleConfig?.MultiChoice?.selectedStyle?.accentColor ||
        this.brandTheme?.primaryColor ||
        this.questThemeData?.accentColor,
      ...this.styleConfig?.SingleChoice?.selectedStyle,
    };
  }

  getMultiChoiceSelectedColorStyles(): React.CSSProperties {
    return {
      color:
        this.styleConfig?.MultiChoice?.selectedStyle?.color ||
        this.brandTheme?.titleColor ||
        this.themeConfig?.primaryColor,
      ...this.styleConfig?.MultiChoice?.selectedStyle,
      background: this.brandTheme?.primaryColor || this.questThemeData?.accentColor,
      borderRadius: this.questThemeData?.borderRadius || this.brandTheme?.borderRadius,
    };
  }

  getMultiChoiceBorderStyle(): React.CSSProperties {
    return {
      border:
        this.themeConfig?.borderColor ||
        this.brandTheme?.borderColor ||
        this.styleConfig?.borderColor ||
        this.themeConfig?.borderColor
    };
  }

  getHeaderStyles() {
    return {
      heading: {
        color:
          this.styleConfig?.Heading?.color ||
          this.brandTheme?.titleColor ||
          this.themeConfig?.primaryColor,
        ...this.styleConfig?.Heading,
      },
      description: {
        color:
          this.styleConfig?.Description?.color ||
          this.brandTheme?.contentColor ||
          this.themeConfig?.secondaryColor,
        ...this.styleConfig?.Description,
      },
    };
  }

  getThanksPopUbIconBackgroundStyle() {
    return (this.brandTheme?.primaryColor?.includes("gradient") ? extractGradientColors(this.brandTheme?.primaryColor) : this.brandTheme?.primaryColor) || (this.questThemeData?.accentColor?.includes("gradient") ? extractGradientColors(this.questThemeData?.accentColor) : this.questThemeData?.accentColor);
  }

  getButtonStyles() {
    return {
      primary: {
        backgroundColor:
          this.brandTheme?.primaryColor ||
          this.questThemeData?.accentColor ||
          this.themeConfig?.buttonColor,
        ...this.styleConfig?.PrimaryButton,
        border:
          this.styleConfig?.PrimaryButton?.border || "none",
        background:
          this.styleConfig?.PrimaryButton?.background ||
          this.brandTheme?.primaryColor ||
          this.questThemeData?.accentColor ||
          this.themeConfig?.buttonColor,
        borderRadius: this.questThemeData?.borderRadius || this.brandTheme?.borderRadius,
        ...this.styleConfig?.PrimaryButton,
      },
      secondary: {
        color:
          this.styleConfig?.SecondaryButton?.color ||
          this.themeConfig?.secondaryColor,
        borderRadius: this.questThemeData?.borderRadius || this.brandTheme?.borderRadius,
        ...this.styleConfig?.SecondaryButton,
      },
    };
  }

  getEmailTextStyles() {
    return {
      text:
        this.styleConfig?.EmailError?.text === undefined
          ? "This is not a valid email"
          : this.styleConfig?.EmailError?.errorStyle || {},
    };
  }
  getEmailErrorStyles() {
    return this.styleConfig?.EmailError?.errorStyle || {};
  }

  getFooterStyles() {
    return {
      style: {
        background:
          this.styleConfig?.Footer?.FooterStyle?.backgroundColor ||
          this.styleConfig?.Form?.backgroundColor ||
          this.styleConfig?.Form?.background ||
          this.brandTheme?.background ||
          this.themeConfig?.backgroundColor,
        borderRadius: this.styleConfig?.Form?.borderRadius || `0px 0px ${this.questThemeData?.borderRadius || this.brandTheme?.borderRadius || "12px"} ${this.questThemeData?.borderRadius || this.brandTheme?.borderRadius || "12px"}`,
        ...this.styleConfig?.Footer?.FooterStyle,
      },
      textStyle: this.styleConfig?.Footer?.FooterTextStyle || {},
      iconStyle: this.styleConfig?.Footer?.FooterIcon || {},
      text: this.styleConfig?.Footer?.FooterText,
      link: this.styleConfig?.Footer?.FooterLink,
    };
  }

  getTopBarStyles() {
    return {
      headingStyle: this.getHeaderStyles().heading,
      descriptionStyle: this.getHeaderStyles().description,
      topbarStyle: {
        ...this.styleConfig?.Topbar,
      },
    };
  }
}
